// =============================================================================
// domain/proAction.ts — may this user DO this, as opposed to SEE this.
// -----------------------------------------------------------------------------
// Relocated from the old client's `requirePro(feature)` (app/index.html:40170)
// and the `isProUser()` guard at the head of `openCoachRun` (:18552).
//
// ── 🚨 WHY A SECOND MECHANISM EXISTS AT ALL ──
//
// #55 ported the tab gate: nine whole surfaces that lock. But the old client's
// paywall has a second half, and it guards the features that live ON FREE TABS:
//
//     Free LOGS and SEES.  Pro DIAGNOSES, FIXES and PROVES.
//
// The Coach's Report is free — a trader must be able to SEE what is costing them
// — and the paid acts sit inside it. The Review and Accounts tabs are free
// and each carries a Coach Me button. A registry field cannot express any of
// that, because the unit being sold is smaller than a tab.
//
// ── 🚨 IT WAS INVISIBLE FOR THE SAME REASON THE TAB GATE WAS ──
//
// `PAID_LAUNCH` is false, so both clients treat everyone as Pro and behave
// identically. A screenshot comparison, the differential harness and every test
// in this repo are equally blind to a gate nobody is holding. It becomes real
// the day paid signups open — in the client that actually launches.
//
// ── ⚠ THIS IS A PRODUCT NUDGE, NOT A SECURITY BOUNDARY, AND SAYING SO MATTERS ──
//
// The old client states it plainly (app/index.html:40188): the things that cost
// real money per use are gated SERVER-side in the Edge Functions, and this layer
// exists so the user meets a pitch instead of a wall.
//
// Measured 2026-09-03 against the live project rather than assumed: the
// `ai-coach` function's own gate is fail-CLOSED — `AI_COACH_ALLOWLIST` is set to
// a small tester list and `AI_COACH_REQUIRE_ENTITLEMENT` is not set at all. So a
// free user's coach click costs $0 today and, at launch with the entitlement
// flag on, still costs $0. **The bill was never the exposure here.** What the
// server cannot do is sell: it answers a refused caller with an error, at the
// exact moment the user asked for the paid thing.
//
// ▶ So the cost of NOT having this is a conversion cost and a trust cost, not a
// spend cost. That is worth stating precisely, because "a free user gets free
// Claude runs" is what the issue said and it is not true.
//
// ── ⚠ THE CATALOGUE IS A LIST, SO A TEST CAN COUNT IT ──
//
// Every gated action is named here once. The alternative — an inline `if (!pro)`
// at each call site — is what the old client has, and it is why its own list had
// to be recovered by grep during the audit. A named catalogue lets a test assert
// that each entry has a caller, which is the check that would have caught #55's
// original defect (a gate nothing read).
// =============================================================================

/**
 * The actions the paid tier is defined by.
 *
 * ⚠ NOT a list of everything Pro. A capability that lives behind a Pro TAB is
 * already gated by the registry, and naming it here too would be a second
 * source of truth for one boundary. This is only for actions on FREE surfaces.
 */
export type ProAction = 'ai-coach' | 'fix-it';

/**
 * 🚨 "PROVE IT" IS DELIBERATELY NOT HERE, AND THE ABSENCE IS THE HONEST ANSWER.
 *
 * It is unmistakably Pro — the pricing table sells "Fix it and Prove it" as the
 * paid half of the methodology — but the old client does not guard it with an
 * action check. It replaces the whole FIX + PROVE half of the Coach's Report
 * with a single lock card (`renderLeakLoopProLock`, app/index.html:43337, chosen
 * at :43372), so a free user never reaches a Prove-it control to click.
 *
 * ✅ That section lock is now ported — `ui/tabs/CoachProLock.tsx`, chosen in
 * `CoachReport.tsx` from the entitlement read. So the absence here is not a gap
 * any more; it is the correct shape. Listing 'prove-it' with no caller would put
 * an entry in a catalogue that the coverage test then has to be taught to skip,
 * which is how a guard becomes decoration.
 *
 * ⚠ AND THE LOCK REACHES WHAT NO ACTION GATE COULD. The paid half of this method
 * is mostly reading — the running rules, each verdict, the ledger — and none of
 * it sits behind a button to refuse.
 *
 * ▶ The rule that chooses that lock is `proSectionLocked`, at the foot of this
 * file. "Prove it" IS checked — as a section, not as a click.
 */

/**
 * What the refusal calls the thing, in the user's words.
 *
 * ⚠ The old client passes this string into `requirePro('Fix it')` at the call
 * site, so a typo there produced a refusal about a feature with a slightly wrong
 * name. Naming them once removes that.
 */
export const PRO_ACTION_LABEL: Readonly<Record<ProAction, string>> = {
  'ai-coach': 'The AI Coach',
  'fix-it': 'Fix it',
};

/**
 * One sentence saying what the reader gets for paying.
 *
 * ⚠ SPECIFIC TO THE ACTION, not a generic "upgrade to Pro". The old client's
 * coach upsell and its leak-loop lock card each make their own argument, and
 * they convert because they name the thing the reader was reaching for.
 */
export const PRO_ACTION_PITCH: Readonly<Record<ProAction, string>> = {
  'ai-coach':
    'Pro reads your own trades — names the leak, writes the rule that closes it, '
    + 'and proves whether it moved your numbers.',
  'fix-it':
    'You can see exactly what is costing you. Pro turns each leak into a testable '
    + 'rule with a frozen baseline, and proves whether it moved the number.',
};

/**
 * May this action run?
 *
 * 🚨 TAKES `pro` RATHER THAN READING ENTITLEMENT. Same reason `hasProAccess`
 * takes `paidLaunch`: a rule that reaches for its own inputs can only be
 * exercised by faking those inputs, and the launched behaviour has to be
 * testable today. The caller has already asked the one question.
 *
 * Returns null when the action may proceed, and the refusal sentence when it may
 * not — the same shape as `freeTierBlocks`, deliberately, so the two refusal
 * paths read alike at their call sites.
 */
export function proActionBlocked(
  args: { action: ProAction; pro: boolean },
): string | null {
  if (args.pro) return null;
  return `${PRO_ACTION_LABEL[args.action]} is a Pro feature.`;
}

// =============================================================================
// SECTIONS — the same question, asked about a block of the page
// =============================================================================
//
// ── 🚨 WHY THIS IS A SECOND RULE AND NOT A THIRD ACTION ──
//
// An action guards a CLICK. A section guards PROSE — a running rule's sentence,
// its verdict, the banked-habits ledger, four panels of analysis. None of it has
// a button in front of it, so there is no click to refuse (D235).
//
// ⚠ AND IT IS HERE, NOT INLINE IN A COMPONENT, FOR ONE REASON: the comparison
// table sells "Prove it" as a Pro benefit, and until this rule had a name that
// promise mapped to nothing. `tierFeatures.test.ts` cross-checks every row of
// that table against the app, and three rows — Fix it, Prove it, AI Coach —
// mapped to an EMPTY list of tabs, so the loop body never ran for them. The
// three cross-cutting promises were the ones nobody was checking (#90).

/** The blocks of a free screen that a free user must not read. */
export type ProSection = 'fix-prove' | 'four-lenses';

/**
 * The heading each lock card carries — the old client's words, verbatim.
 *
 * ⚠ THE WHOLE SENTENCE, not a name to interpolate. "Fix it & Prove it ARE Pro"
 * and "The full account analysis IS Pro" do not share a template, and building
 * one would produce a grammatical error on a sales surface.
 *
 * ⚠ AND IT LIVES HERE SO THE CATALOGUE IS LOAD-BEARING. A list of section names
 * that nothing in `ui/` ever names is decoration, and the coverage test below
 * could not tell it apart from a section nobody locked.
 */
export const PRO_SECTION_TITLE: Readonly<Record<ProSection, string>> = {
  'fix-prove': 'Fix it & Prove it are Pro',
  'four-lenses': 'The full account analysis is Pro',
};

/**
 * Should this section be replaced by a lock?
 *
 * 🚨 `pending` IS READ IN THE OPPOSITE DIRECTION FROM `proActionBlocked`, AND
 * THE ASYMMETRY IS THE WHOLE REASON THIS IS A SEPARATE FUNCTION.
 *
 *   an ACTION, mid-read → ALLOW. Nothing is revealed until the click, and
 *                         refusing a paying customer at the moment they reach
 *                         for what they bought is a support ticket and a refund.
 *   a SECTION, mid-read → DRAW NEITHER. Access fails OPEN on an unknown read, so
 *                         drawing it would flash the paid content at a free user
 *                         on every single visit.
 *
 * So this returns false while `pending`, and the caller must render its
 * "checking" state instead of the real content — `locked === false` is NOT
 * permission to draw. That is the one trap in this signature, and it is why the
 * caller reads `pending` too rather than this answer alone.
 *
 * ⚠ Takes both rather than reading entitlement itself, for the same reason
 * `proActionBlocked` takes `pro`: a rule that reaches for its own inputs can
 * only be exercised by faking them.
 */
export function proSectionLocked(
  args: { pro: boolean; pending: boolean },
): boolean {
  if (args.pending) return false;
  return !args.pro;
}
